import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Globe, Handshake, Briefcase, ChartBar } from "@phosphor-icons/react";
import { usePresenceMap } from "@/lib/usePresenceMap";
import { PresenceDot } from "@/components/site/PresenceDot";

/* ============================================================================
   CoeWorkloadTab — per-staff open-lead distribution.
   ----------------------------------------------------------------------------
   Helps the admin spot:
     • overloaded reps (high totals)
     • staff with imbalanced kind mix (e.g. 0 web but 12 b2b)
     • idle members (0 open work)
   Powers the future "smart assignee" suggestion when assigning new leads.
   ========================================================================== */

export function CoeWorkloadTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const { statusFor } = usePresenceMap();

    useEffect(() => {
        api.get("/admin/coe/workload").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
    }, []);

    const max = Math.max(1, ...rows.map((r) => r.open_total));

    return (
        <div className="space-y-5" data-testid="coe-workload-tab">
            <div className="rounded-2xl border border-border bg-card p-5">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-3">
                    Current workload per active member
                </div>
                {loading ? (
                    <div className="text-muted-foreground text-sm text-center py-8">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="text-muted-foreground text-sm text-center py-8">No active members yet.</div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((r) => {
                            const w = r.open_web, b = r.open_b2b, a = r.open_application, t = r.open_total;
                            return (
                                <div key={r.id} className="grid grid-cols-12 gap-3 items-center" data-testid={`coe-workload-row-${r.id}`}>
                                    <div className="col-span-4 lg:col-span-3 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <PresenceDot status={statusFor(r.id)} size="sm" />
                                            <div className="font-semibold text-sm truncate">{r.name}</div>
                                        </div>
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mt-0.5">{r.role_label}</div>
                                    </div>
                                    <div className="col-span-6 lg:col-span-7">
                                        <div className="h-3 rounded-full bg-secondary overflow-hidden flex">
                                            <div className="h-full bg-sky-500" style={{ width: `${(w / max) * 100}%` }} title={`${w} web`} />
                                            <div className="h-full bg-violet-500" style={{ width: `${(b / max) * 100}%` }} title={`${b} B2B`} />
                                            <div className="h-full bg-amber-500" style={{ width: `${(a / max) * 100}%` }} title={`${a} apps`} />
                                        </div>
                                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1">
                                            <span className="inline-flex items-center gap-1"><Globe size={9} /> {w}</span>
                                            <span className="inline-flex items-center gap-1"><Handshake size={9} /> {b}</span>
                                            <span className="inline-flex items-center gap-1"><Briefcase size={9} /> {a}</span>
                                        </div>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <div className="text-2xl font-display font-bold tabular-nums leading-none">{t}</div>
                                        <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">open</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="rounded-xl border border-border bg-secondary/30 p-4 flex items-start gap-3">
                <ChartBar size={16} weight="duotone" className="text-primary mt-0.5 shrink-0" />
                <div className="text-xs text-muted-foreground">
                    Workload reflects the count of OPEN leads currently owned by each member across web · B2B · applications. The Leads Hub uses these numbers to suggest the least-loaded eligible assignee when admin distributes new leads.
                </div>
            </div>
        </div>
    );
}
